import React, { forwardRef, useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { Calendar, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { registerLocale } from 'react-datepicker';
import { it } from 'date-fns/locale';
import { clsx } from 'clsx';

import 'react-datepicker/dist/react-datepicker.css';
import './date-range-picker.scss';

// Create custom Italian locale with single-letter day names
const customIt = {
  ...it,
  localize: {
    ...it.localize,
    day: (dayIndex: number) => ['D', 'L', 'M', 'M', 'G', 'V', 'S'][dayIndex],
  },
};

// Register custom Italian locale
registerLocale('it', customIt as any);

interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  className?: string;
  disabled?: boolean;
  labels?: {
    startDate: string;
    endDate: string;
    selectStartDate: string;
    selectEndDate: string;
  };
}

interface CustomInputProps {
  value?: string;
  onClick?: () => void;
  placeholder?: string;
  disabled?: boolean;
}

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(({ value, onClick, placeholder, disabled }, ref) => {
  return (
    <div className="date-range-input" onClick={onClick} ref={ref}>
      <Calendar size={16} className="date-range-calendar-icon" />
      <div className={clsx('date-range-input-value', { disabled })} onClick={onClick}>
        {value ?? placeholder}
      </div>
    </div>
  );
});

CustomInput.displayName = 'CustomInput';

interface CustomHeaderProps {
  date: Date;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
  decreaseYear: () => void;
  increaseYear: () => void;
  prevYearButtonDisabled: boolean;
  nextYearButtonDisabled: boolean;
  onMonthClick: () => void;
  onYearClick: () => void;
  onBackToDate: () => void;
  locale: string;
  viewMode: 'date' | 'month' | 'year';
}

const CustomHeader: React.FC<CustomHeaderProps> = ({
  date,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  decreaseYear,
  increaseYear,
  prevYearButtonDisabled,
  nextYearButtonDisabled,
  onMonthClick,
  onYearClick,
  onBackToDate,
  locale,
  viewMode,
}) => {
  // Get month name based on locale
  const getMonthName = (date: Date): string => {
    return date.toLocaleString(locale, { month: 'long' });
  };

  const isYearMode = viewMode === 'year';
  const isMonthMode = viewMode === 'month';

  // Determine navigation handlers based on view mode
  const handlePrevClick = isYearMode ? decreaseYear : isMonthMode ? onBackToDate : decreaseMonth;
  const handleNextClick = isYearMode ? increaseYear : increaseMonth;
  const isPrevDisabled = isYearMode ? prevYearButtonDisabled : isMonthMode ? false : prevMonthButtonDisabled;
  const isNextDisabled = isYearMode ? nextYearButtonDisabled : nextMonthButtonDisabled;
  const showNextButton = !isMonthMode; // Hide next button only in month mode

  return (
    <div className="custom-header-container">
      <button
        type="button"
        onClick={handlePrevClick}
        disabled={isPrevDisabled}
        className="nav-button"
      >
        <ChevronLeft size={16} viewBox="3 3 18 18" />
      </button>
      <div className="month-year-selectors">
        <button onClick={onMonthClick} className="month-selector">
          {getMonthName(date)}
        </button>
        <button onClick={onYearClick} className="year-selector">
          {date.getFullYear()}
        </button>
      </div>
      {showNextButton && (
        <button type="button" onClick={handleNextClick} disabled={isNextDisabled} className="nav-button">
          <ChevronRight size={16} viewBox="3 3 18 18" />
        </button>
      )}
      {!showNextButton && <div className="nav-button-placeholder" />}
    </div>
  );
};

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  className = '',
  disabled = false,
  labels = {
    startDate: 'Start Date',
    endDate: 'End Date',
    selectStartDate: 'gg/mm/aaaa',
    selectEndDate: 'gg/mm/aaaa',
  },
}) => {
  // View states for both date pickers
  const [startDateView, setStartDateView] = useState<'date' | 'month' | 'year'>('date');
  const [endDateView, setEndDateView] = useState<'date' | 'month' | 'year'>('date');

  // Temporary state while navigating through year/month/date pickers
  const [tempStartDate, setTempStartDate] = useState<Date | null>(startDate);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(endDate);

  // Get the current locale from browser or use 'en' as default
  const [currentLocale] = useState<string>('it');

  // Sync temp state with prop changes
  useEffect(() => {
    setTempStartDate(startDate);
  }, [startDate]);

  useEffect(() => {
    setTempEndDate(endDate);
  }, [endDate]);

  // Date format based on view
  const getDateFormat = (view: 'date' | 'month' | 'year'): string => {
    switch (view) {
      case 'month':
        return 'MM/yyyy';
      case 'year':
        return 'yyyy';
      default:
        return 'dd/MM/yyyy';
    }
  };

  return (
    <div className={`date-range-picker-container ${className}`}>
      <div className="date-range-picker-inputs">
        <div className="date-input-group">
          <label className="date-input-label">{labels.startDate}</label>
          <div className="date-input-wrapper">
            <DatePicker
              selected={tempStartDate}
              onChangeRaw={(e) => {
                // Prevent closing when typing
                e.preventDefault();
              }}
              onChange={(date) => {
                // Handle single date selection
                const selectedDate = Array.isArray(date) ? date[0] : date;

                if (startDateView === 'year') {
                  // When selecting a year, update temp state and go to month picker
                  setTempStartDate(selectedDate);
                  setStartDateView('month');
                } else if (startDateView === 'month') {
                  // When selecting a month, update temp state and go to date picker
                  setTempStartDate(selectedDate);
                  setStartDateView('date');
                } else {
                  // When selecting a date, update parent (final selection)
                  onStartDateChange(selectedDate);
                  setTempStartDate(selectedDate);
                }
              }}
              onMonthChange={(date) => {
                // Force view change even if same month is selected
                if (startDateView === 'month') {
                  setTempStartDate(date);
                  setStartDateView('date');
                }
              }}
              onCalendarOpen={() => {
                // Reset to date view when opening picker
                setStartDateView('date');
                setTempStartDate(startDate);
              }}
              onCalendarClose={() => {
                // Update parent with temp date when closing if it's different
                if (tempStartDate !== startDate) {
                  onStartDateChange(tempStartDate);
                }
              }}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              // @ts-expect-error - react-datepicker type issue with selectsStart and selectsMultiple
              selectsMultiple={false}
              dateFormat={getDateFormat(startDateView)}
              customInput={<CustomInput />}
              placeholderText={labels.selectStartDate}
              shouldCloseOnSelect={startDateView === 'date'}
              disabled={disabled}
              showMonthYearPicker={startDateView === 'month'}
              showYearPicker={startDateView === 'year'}
              locale={currentLocale}
              popperPlacement="bottom-start"
              showPopperArrow={false}
              renderCustomHeader={(props) => (
                <CustomHeader
                  {...props}
                  onMonthClick={() => setStartDateView('month')}
                  onYearClick={() => setStartDateView('year')}
                  onBackToDate={() => setStartDateView('date')}
                  locale={currentLocale}
                  viewMode={startDateView}
                />
              )}
              minDate={new Date(2000, 0, 1)}
              maxDate={endDate || new Date()}
            />
            {startDate && (
              <button
                type="button"
                className="date-clear-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onStartDateChange(null);
                }}
                disabled={disabled}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        <div className="date-input-group">
          <label className="date-input-label">{labels.endDate}</label>
          <div className="date-input-wrapper">
            <DatePicker
              selected={tempEndDate}
              onChangeRaw={(e) => {
                // Prevent closing when typing
                e.preventDefault();
              }}
              onChange={(date) => {
                // Handle single date selection
                const selectedDate = Array.isArray(date) ? date[0] : date;

                if (endDateView === 'year') {
                  // When selecting a year, update temp state and go to month picker
                  setTempEndDate(selectedDate);
                  setEndDateView('month');
                } else if (endDateView === 'month') {
                  // When selecting a month, update temp state and go to date picker
                  setTempEndDate(selectedDate);
                  setEndDateView('date');
                } else {
                  // When selecting a date, update parent (final selection)
                  onEndDateChange(selectedDate);
                  setTempEndDate(selectedDate);
                }
              }}
              onMonthChange={(date) => {
                // Force view change even if same month is selected
                if (endDateView === 'month') {
                  setTempEndDate(date);
                  setEndDateView('date');
                }
              }}
              onCalendarOpen={() => {
                // Reset to date view when opening picker
                setEndDateView('date');
                setTempEndDate(endDate);
              }}
              onCalendarClose={() => {
                // Update parent with temp date when closing if it's different
                if (tempEndDate !== endDate) {
                  onEndDateChange(tempEndDate);
                }
              }}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              // @ts-expect-error - react-datepicker type issue with selectsEnd and selectsMultiple
              selectsMultiple={false}
              minDate={startDate || new Date(2000, 0, 1)}
              maxDate={new Date(new Date().setDate(new Date().getDate() - 1))}
              dateFormat={getDateFormat(endDateView)}
              customInput={<CustomInput />}
              placeholderText={labels.selectEndDate}
              shouldCloseOnSelect={endDateView === 'date'}
              disabled={disabled}
              showMonthYearPicker={endDateView === 'month'}
              showYearPicker={endDateView === 'year'}
              locale={currentLocale}
              popperPlacement="bottom-start"
              showPopperArrow={false}
              renderCustomHeader={(props) => (
                <CustomHeader
                  {...props}
                  onMonthClick={() => setEndDateView('month')}
                  onYearClick={() => setEndDateView('year')}
                  onBackToDate={() => setEndDateView('date')}
                  locale={currentLocale}
                  viewMode={endDateView}
                />
              )}
            />
            {endDate && (
              <button
                type="button"
                className="date-clear-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEndDateChange(null);
                }}
                disabled={disabled}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;
